// PDF pipeline: greedy block-level pagination (marginTop injection) → html2canvas snapshot → slice at page boundaries → pdf-lib.
// Principle: if it renders in the editor, it renders in the PDF (html2canvas captures live DOM styles).
// `exportEditorToPdf` (the raster path below) has no production caller since the server-export
// fallback was removed (RECUR-26973) — kept only for the shared pagination model + its own tests; excising it is a tracked follow-up.

import { urlToDataUrl } from './exportHelpers';
import { PAGE_WIDTH_PT, MARGIN_PT, PDF_CONTENT_WIDTH_PT, PDF_PAGE_ASPECT, getDefaultPageHeightPx } from './pageGeometry';

// Re-exported so existing importers of getDefaultPageHeightPx from this
// module keep working after the constants moved to pageGeometry.ts
// (RECUR-27304) — pure move, no behavior change.
export { getDefaultPageHeightPx };

interface ExportToPdfOptions {
    target: HTMLElement;
    filename?: string;
    /**
     * When true, skips `planPages` and emits ONE tall PDF page covering the
     * full editor content. Trades off pagination correctness (no widow/orphan
     * handling, no sticky-heading logic — there's just no boundary to fight)
     * against viewer compatibility (Adobe Acrobat caps page height at
     * 14400 pt ≈ 200 in; many viewers handle more but it varies). Use for
     * "give me the whole thing as a scroll" exports where pagination is
     * actively unwanted.
     */
    singlePage?: boolean;
    /** Return the rendered PDF as a Blob and skip the browser download. */
    returnBlob?: boolean;
    /**
     * EXPERIMENTAL (M1): capture the document in multiple bounded "tiles" rather
     * than one giant canvas, so very long docs (25+ pages) don't hit the
     * browser's canvas-height limit (Safari 16384px/side) and silently truncate.
     * When false/undefined the original single-canvas path runs UNCHANGED — this
     * is purely additive. Mutually exclusive with `singlePage`.
     */
    tiled?: boolean;
}

const SNAPSHOT_SCALE = 3; // N=3 for sharp text; safe up to ~5400 CSS px tall documents.
const PAGE_TOP_PADDING_CSS = 48; // ~0.5in inner page padding
const PAGE_BOTTOM_PADDING_CSS = 48;
// Floor for a committed page's height. Pages are now sized to their actual
// content (no default-height padding — that floor was the source of trailing
// whitespace), but a page must never be degenerately short: a single one-line
// page would otherwise be ~PAGE_TOP_PADDING_CSS + a line-height tall, which
// reads as a torn scrap in the viewer. This minimum is a sane page stub, NOT a
// target — content taller than it always wins. Deliberately well below a full
// page so it never re-introduces bottom gaps.
const MIN_PAGE_HEIGHT_CSS = PAGE_TOP_PADDING_CSS + PAGE_BOTTOM_PADDING_CSS + 120;

// Content-sized page height: the natural content height plus bottom padding,
// floored at MIN_PAGE_HEIGHT_CSS so a near-empty page isn't degenerate. NEVER
// pads up to a full default page — that floor was the trailing-whitespace bug.
// Pure + testable (the old `Math.max(naturalPageHeight, defaultPageHeightCSS)`
// inline math is replaced by this everywhere a page is committed).
export function contentSizedPageHeight(contentHeightCSS: number): number {
    const naturalHeight = contentHeightCSS + PAGE_BOTTOM_PADDING_CSS;
    return Math.max(naturalHeight, MIN_PAGE_HEIGHT_CSS);
}

// ── Raster glyph-drop fix ───────────────────────────────────────────────────
// html2canvas-pro drops glyphs from a backgrounded inline (<mark> or
// span[style*="background-color"]) that WRAPS across many visual lines: a word
// whose Range yields >1 client rect (a wrap artifact) trips a fragile grapheme
// re-measure in parseTextBounds, and renderNodeContent clips content to the
// inline's contentBox — a wrapped inline's geometry is exactly where line
// fragments fall outside the clip. A SHORT, single-line backgrounded run renders
// perfectly (field-proven: a one-line highlight survives, the 30-line one drops).
// So on the capture clone we rewrite each long highlighted run into per-word,
// single-line clones of itself — each is a one-rect box that takes the clean
// path. Clone-only (the live editor is untouched), and we split on EXISTING
// whitespace so line-break opportunities — and thus the pagination measured on
// the live DOM — are preserved. box-decoration-break (rasterFixStyle) stays as
// the fallback for the nested/rare cases this skips.
const HIGHLIGHT_SPLIT_MIN_LEN = 80;

export function splitHighlightRunsForRaster(root: HTMLElement): void {
    const runs = root.querySelectorAll<HTMLElement>('mark, span[style*="background-color"]');
    runs.forEach((el) => {
        const text = el.textContent ?? '';
        if (text.length <= HIGHLIGHT_SPLIT_MIN_LEN) return;
        // A real highlight is NOT a raw text node — Slate wraps every leaf's text
        // in <span data-slate-string> (sometimes a data-slate-leaf span too), so
        // the run is e.g. <mark><span data-slate-string>…</span></mark>. Flatten
        // those wrappers (the clone is a throwaway raster snapshot; the slate
        // data-attrs aren't needed). Skip ONLY when the run holds genuine nested
        // formatting — a non-slate element, or a span with its own inline style
        // (bold / link / colored span) — that a shallow clone would destroy; those
        // rare cases fall back to the box-decoration-break CSS.
        const flattenable = Array.from(el.querySelectorAll('*')).every((d) => {
            const isSlateWrapperSpan = d.tagName === 'SPAN' && Array.from(d.attributes).some((a) => a.name.startsWith('data-slate'));
            const hasInlineStyle = !!(d.getAttribute('style') || '').trim();
            return isSlateWrapperSpan && !hasInlineStyle;
        });
        if (!flattenable) return;
        // Keep whitespace as its own tokens so the highlight stays continuous
        // across spaces (a highlighted space still paints its background box).
        const tokens = text.split(/(\s+)/).filter((t) => t.length > 0);
        if (tokens.length < 2) return;
        const frag = el.ownerDocument.createDocumentFragment();
        for (const tok of tokens) {
            const piece = el.cloneNode(false) as HTMLElement;
            piece.textContent = tok;
            frag.appendChild(piece);
        }
        el.replaceWith(frag);
    });
}

// ── Tiled capture (M1) ───────────────────────────────────────────────────────
// Max canvas pixels per capture tile. Kept under Safari's 16384 px/side wall
// (the binding limit; Chrome 65535/side, Firefox 32767/side) with headroom for
// clone reflow. At SNAPSHOT_SCALE=3 this is ~4666 CSS px ≈ 4 A3 pages per tile,
// so a 25-page doc captures in ~7 calls instead of one over-tall canvas.
export const MAX_TILE_CANVAS_PX = 14000;

/** One capture tile: a contiguous band of page ranges captured into one canvas. */
export interface Tile {
    /** Band start in live CSS px (equals the first range's topCSS). */
    topCSS: number;
    /** Band height in live CSS px. `heightCSS * scale` stays ≤ maxTileCanvasPx
     *  unless a single range is itself taller than the ceiling. */
    heightCSS: number;
    /** First `ranges[]` index covered by this tile. */
    pageIdxStart: number;
    /** Last `ranges[]` index covered (inclusive). */
    pageIdxEnd: number;
}

/**
 * Chunk planPages page ranges into capture tiles, each bounded so its rasterized
 * canvas height (`heightCSS * scale`) stays under `maxTileCanvasPx`. Pure +
 * deterministic.
 *
 * Tiles ALWAYS close at a page boundary, so a tile seam is never mid-content —
 * only INTERIOR page boundaries within a tile need blank-row snapping later.
 * A single range taller than the ceiling becomes its own (oversized) tile; the
 * caller's scale guard (a later stage) handles that degenerate case rather than
 * splitting a block mid-content.
 *
 * Assumes `ranges` are contiguous + ordered (planPages guarantees this), so a
 * tile's `heightCSS` is just the sum of its ranges' heights.
 */
/**
 * The maximum CSS height a single capture tile can hold at a given scale —
 * `maxTileCanvasPx / scale`. Above this a single html2canvas canvas hits the
 * browser's canvas-height wall (Safari ~16384px/side) and SILENTLY TRUNCATES
 * the bottom. Pure helper so the auto-tile decision and the oversized-range
 * guard share one definition of "safe".
 */
export function maxTileHeightCSS(maxTileCanvasPx: number, scale: number): number {
    return maxTileCanvasPx / Math.max(scale, 1);
}

/**
 * Decide whether tiling MUST be enabled for this document, independent of the
 * caller's `tiled` flag. A single html2canvas canvas truncates past the canvas
 * wall, so we auto-tile when EITHER the total content height OR any single page
 * range would exceed the safe per-tile CSS height. Pure + deterministic so the
 * decision is unit-testable (jsdom can't measure layout, but the math can).
 *
 * Returns true if tiling is required. The caller ORs this with the explicit
 * `tiled` option, so an explicit `tiled: true` still tiles a short doc.
 */
export function shouldAutoTile(ranges: PageRange[], maxTileCanvasPx: number, scale: number): boolean {
    if (!Array.isArray(ranges) || ranges.length === 0) return false;
    const ceilingCSS = maxTileHeightCSS(maxTileCanvasPx, scale);
    const totalHeightCSS = ranges.reduce((sum, r) => sum + r.heightCSS, 0);
    if (totalHeightCSS > ceilingCSS) return true;
    return ranges.some((r) => r.heightCSS > ceilingCSS);
}

/**
 * Find page ranges that are individually taller than a single tile can capture
 * (`maxTileCanvasPx / scale` CSS px). Such a range is a pathological single
 * oversized group (e.g. a table taller than ~4,666 CSS px at scale 3): it can
 * NOT be rasterized whole without hitting the canvas wall, and slicing it would
 * split the group mid-row. The caller auto-scales the capture down (smaller but
 * COMPLETE) or, failing that, throws — it must never silently truncate. Pure +
 * testable. Returns the indices of oversized ranges.
 */
export function findOversizedRanges(ranges: PageRange[], maxTileCanvasPx: number, scale: number): number[] {
    if (!Array.isArray(ranges) || ranges.length === 0) return [];
    const ceilingCSS = maxTileHeightCSS(maxTileCanvasPx, scale);
    const oversized: number[] = [];
    for (let i = 0; i < ranges.length; i++) {
        if (ranges[i].heightCSS > ceilingCSS) oversized.push(i);
    }
    return oversized;
}

/**
 * Compute the capture scale needed so the tallest single range fits within one
 * tile. When a range exceeds the per-tile ceiling at the requested scale, we
 * reduce the scale for the WHOLE capture (uniform scale keeps tile math + pxToPt
 * simple) just enough that the tallest range's rasterized height equals the
 * ceiling: `scale' = maxTileCanvasPx / tallestRangeHeightCSS`. Returns the
 * requested scale unchanged when nothing is oversized. Floors the result at a
 * minimum so we don't degrade to unreadable mush — if even the floor can't fit
 * the range, the caller throws (better than a silently truncated page). Pure +
 * testable.
 */
export const MIN_AUTOSCALE = 1; // below this, text is unreadable — throw instead.
export function scaleToFitTallestRange(ranges: PageRange[], maxTileCanvasPx: number, requestedScale: number): number {
    if (!Array.isArray(ranges) || ranges.length === 0) return requestedScale;
    const tallest = ranges.reduce((max, r) => Math.max(max, r.heightCSS), 0);
    if (tallest <= 0) return requestedScale;
    const ceilingCSS = maxTileHeightCSS(maxTileCanvasPx, requestedScale);
    if (tallest <= ceilingCSS) return requestedScale;
    // Reduce scale so tallest * scale' === maxTileCanvasPx.
    return maxTileCanvasPx / tallest;
}

export function buildTiles(ranges: PageRange[], maxTileCanvasPx: number, scale: number): Tile[] {
    const tiles: Tile[] = [];
    if (!Array.isArray(ranges) || ranges.length === 0) return tiles;

    const maxTileCss = maxTileCanvasPx / Math.max(scale, 1);
    let startIdx = 0;
    let topCSS = ranges[0].topCSS;
    let accHeight = 0;

    for (let i = 0; i < ranges.length; i++) {
        const rangeH = ranges[i].heightCSS;
        // Close the current tile BEFORE this range if adding it would overflow
        // the ceiling — but only when the tile already holds ≥1 range, so a
        // lone over-tall range still gets its own (oversized) tile.
        if (accHeight > 0 && accHeight + rangeH > maxTileCss) {
            tiles.push({ topCSS, heightCSS: accHeight, pageIdxStart: startIdx, pageIdxEnd: i - 1 });
            startIdx = i;
            topCSS = ranges[i].topCSS;
            accHeight = 0;
        }
        accHeight += rangeH;
    }
    tiles.push({ topCSS, heightCSS: accHeight, pageIdxStart: startIdx, pageIdxEnd: ranges.length - 1 });
    return tiles;
}

/**
 * Snap a planned page-break Y (in canvas px) to the nearest all-white
 * horizontal scanline within ±windowPx, PREFERRING upward.
 *
 * Why upward bias: the live-space planned boundary computed by `planPages`
 * drifts (the rasterized clone re-lays-out slightly taller as you go down the
 * doc). When a boundary drifts INTO a heading's glyphs, the blank band we want
 * to cut at is the heading's top-margin gap, which sits just ABOVE the drifted
 * boundary. Snapping up therefore moves the whole heading to the next page.
 *
 * Search strategy: probe distance 1, 2, 3 … up to windowPx; at each distance
 * check the upward candidate first, then the downward candidate. First blank
 * row found wins. If none in-window, return `plannedY` unchanged (degrades to
 * the current cut location — never worse).
 *
 * Pure function: `isBlank` is injected so the pixel-reading wrapper (which
 * needs a real canvas) stays out of this testable math.
 */
export function snapToBlankRow(isBlank: (y: number) => boolean, plannedY: number, windowPx: number, maxY: number): number {
    const clamp = (y: number) => Math.max(0, Math.min(maxY, y));
    const start = clamp(Math.round(plannedY));
    if (isBlank(start)) return start;
    const limit = Math.max(0, Math.round(windowPx));
    for (let d = 1; d <= limit; d++) {
        const up = start - d;
        if (up >= 0 && isBlank(up)) return up;
        const down = start + d;
        if (down <= maxY && isBlank(down)) return down;
    }
    return start;
}

interface InlineStyleBackup {
    el: HTMLElement;
    overflow: string;
    overflowY: string;
    maxHeight: string;
    height: string;
}

function expandForCapture(target: HTMLElement): InlineStyleBackup[] {
    const backups: InlineStyleBackup[] = [];
    const contentEl = target.querySelector('[data-testid="plate-editor-content"]') as HTMLElement | null;
    const chain: HTMLElement[] = [];
    if (contentEl) chain.push(contentEl);
    let node: HTMLElement | null = contentEl?.parentElement ?? null;
    while (node && node !== target) {
        chain.push(node);
        node = node.parentElement;
    }
    if (target) chain.push(target);

    for (const el of chain) {
        backups.push({
            el,
            overflow: el.style.overflow,
            overflowY: el.style.overflowY,
            maxHeight: el.style.maxHeight,
            height: el.style.height,
        });
        el.style.overflow = 'visible';
        el.style.overflowY = 'visible';
        el.style.maxHeight = 'none';
        el.style.height = 'auto';
    }
    return backups;
}

function restoreAfterCapture(backups: InlineStyleBackup[]) {
    for (const b of backups) {
        b.el.style.overflow = b.overflow;
        b.el.style.overflowY = b.overflowY;
        b.el.style.maxHeight = b.maxHeight;
        b.el.style.height = b.height;
    }
}

// Resolve data-doc-id images to base64 via presigned URLs (CORS workaround).
async function resolveDocIdImagesToBase64(contentEl: HTMLElement): Promise<Map<string, string>> {
    const map = new Map<string, string>();
    const docIdSet = new Set<string>();
    contentEl.querySelectorAll<HTMLImageElement>('img[data-doc-id]').forEach((img) => {
        const id = img.getAttribute('data-doc-id');
        if (id) docIdSet.add(id);
    });
    if (docIdSet.size === 0) return map;

    const helpers = await import('../../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers');
    const docIds = Array.from(docIdSet)
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n));
    if (docIds.length === 0) return map;

    const bulk = await helpers.getPresignedUrlBulk(docIds);
    const failures: string[] = [];
    await Promise.all(
        docIds.map(async (idNum) => {
            const presignedUrl = bulk[idNum];
            if (!presignedUrl) {
                failures.push(`docId=${idNum} (no presigned URL returned)`);
                return;
            }
            try {
                const dataUrl = await urlToDataUrl(presignedUrl);
                map.set(String(idNum), dataUrl);
            } catch (err: any) {
                failures.push(`docId=${idNum}: ${String(err?.message || err)}`);
            }
        }),
    );
    return map;
}

async function preloadImagesAsDataUrls(contentEl: HTMLElement): Promise<Map<string, string>> {
    const imgs = Array.from(contentEl.querySelectorAll('img')) as HTMLImageElement[];
    const unique = new Set<string>();
    imgs.forEach((img) => {
        const src = img.getAttribute('src') || img.src;
        if (src && /^https?:/i.test(src)) unique.add(src);
    });
    const map = new Map<string, string>();
    const failures: Array<{ url: string; error: string }> = [];
    await Promise.all(
        Array.from(unique).map(async (url) => {
            try {
                map.set(url, await urlToDataUrl(url));
            } catch (err: any) {
                failures.push({ url: url.slice(0, 100), error: String(err?.message || err) });
            }
        }),
    );
    return map;
}

interface LinkRect {
    url: string;
    /** Coords in CANVAS pixels, relative to the snapshot canvas's top-left. */
    x: number;
    y: number;
    w: number;
    h: number;
}

// `scale` must equal the ACTUAL capture scale used for html2canvas — link rects
// are stored in canvas px, so an auto-scaled-down capture (oversized-group case)
// needs links at the same reduced scale or the annotations land off-target.
function collectLinks(contentEl: HTMLElement, scale: number): LinkRect[] {
    const links: LinkRect[] = [];
    const contentRect = contentEl.getBoundingClientRect();
    contentEl.querySelectorAll('a[href]').forEach((node) => {
        const a = node as HTMLAnchorElement;
        const href = a.href;
        if (!href || href === '#' || href.startsWith('javascript:')) return;
        const rects = a.getClientRects();
        Array.from(rects).forEach((r) => {
            links.push({
                url: href,
                x: (r.left - contentRect.left) * scale,
                y: (r.top - contentRect.top) * scale,
                w: r.width * scale,
                h: r.height * scale,
            });
        });
    });
    return links;
}

/** A computed page range in CSS px relative to `contentEl`'s top edge. */
interface PageRange {
    topCSS: number;
    heightCSS: number;
}

/** A reversible marginTop edit applied to a block element. */
interface MarginEdit {
    el: HTMLElement;
    previous: string;
    /** Optional reversible `display` change. When set, restoreMarginEdits also
     *  restores `display` along with `marginTop`. Used to temporarily hide
     *  trailing empty paragraphs so the rasterized canvas doesn't carry their
     *  whitespace into the PDF as a blank page. */
    previousDisplay?: string;
}

// Uses offsetTop (not summed offsetHeight) because CSS margins between blocks would silently drift computed boundaries.
// Headings stick with the next block to prevent orphan titles at page bottoms.
const STICKY_PRECEDING_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
// Walk back at most this many blocks looking for a heading to pull along. A
// limit is needed so we don't drag an entire previous section forward when the
// heading was many blocks back.
const MAX_STICKY_LOOKBACK = 8;
// Block types that visually break a sticky group when found between heading
// and table — we don't drag a sibling table or media along with another table.
const GROUP_BREAK_TAGS = new Set(['TABLE', 'HR', 'IMG', 'FIGURE']);
// Tags that TERMINATE a sticky group (inclusive) during the group-building pass.
// Includes UL/OL so heading + bulleted-list pairs receive the same orphan
// protection as heading + table.
const GROUP_END_TAGS = new Set(['TABLE', 'HR', 'IMG', 'FIGURE', 'UL', 'OL']);

function isStickyPrecedingBlock(block: HTMLElement): boolean {
    if (STICKY_PRECEDING_TAGS.has(block.tagName)) return true;
    const slateType = block.getAttribute('data-slate-type') || '';
    return (
        slateType === 'h1' ||
        slateType === 'h2' ||
        slateType === 'h3' ||
        slateType === 'h4' ||
        slateType === 'h5' ||
        slateType === 'h6' ||
        slateType === 'heading'
    );
}

interface PaginationGroup {
    startIdx: number;
    endIdx: number; // inclusive
}

// Build sticky groups from the block list before the pagination loop.
// A group is either:
//   • a sticky heading (h1-h6) + following blocks up to and including the next
//     GROUP_END_TAG or the start of the next heading (whichever comes first), OR
//   • a single standalone block (paragraph, plain content).
// Groups are capped at MAX_STICKY_LOOKBACK + 1 blocks so we never drag an
// entire previous section forward. This pre-pass replaces findStickyGroupStart
// / canPullGroup: the heading is part of its group by construction, so the
// "heading is first on page" guard that caused the orphaned-heading bug is no
// longer needed.
//
// Exported (alongside the existing `planEmptyBlockHides` precedent) so its
// boundary behaviour is unit-testable without booting layout/html2canvas.
export function buildPaginationGroups(blocks: HTMLElement[]): PaginationGroup[] {
    const groups: PaginationGroup[] = [];
    let i = 0;
    while (i < blocks.length) {
        if (isStickyPrecedingBlock(blocks[i])) {
            let endIdx = i;
            const cap = Math.min(i + MAX_STICKY_LOOKBACK, blocks.length - 1);
            for (let j = i + 1; j <= cap; j++) {
                if (isStickyPrecedingBlock(blocks[j])) break; // next heading starts a new group
                endIdx = j;
                if (GROUP_END_TAGS.has(blocks[j].tagName)) break; // group terminates AT this block
            }
            groups.push({ startIdx: i, endIdx });
            i = endIdx + 1;
        } else {
            groups.push({ startIdx: i, endIdx: i });
            i++;
        }
    }
    return groups;
}

// Augmented range type used internally to track natural content height for
// trailing-empty-range pruning. The _contentHeight field is stripped before
// returning to callers.
interface PageRangeInternal extends PageRange {
    _contentHeight: number;
}

const PLAN_PAGES_DEBUG = false;
function plog(..._args: any[]) {}
function plogGroup(_label: string) {}
function plogGroupEnd() {}
function describeBlock(b: HTMLElement): string {
    const slateType = b.getAttribute('data-slate-type') || '';
    const tag = b.tagName;
    const text = (b.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 60);
    const label = slateType ? `${tag}.${slateType}` : tag;
    return text ? `${label} "${text}${(b.textContent || '').length > 60 ? '…' : ''}"` : label;
}

// Detect a "visually empty" block — an empty paragraph the editor renders to
// give the caret a landing spot, but which produces no real content in the PDF.
// We treat as empty: no non-whitespace text, no images, no tables, no embeds.
// Used to trim trailing emptiness so the PDF doesn't get a blank tail page.
function isVisuallyEmptyBlock(el: HTMLElement): boolean {
    const text = (el.textContent || '').trim();
    if (text.length > 0) return false;
    // Any media / structural descendant means "not empty"
    if (el.querySelector('img, video, table, figure, iframe, hr, [data-doc-id], [data-slate-type="image"], [data-slate-type="embed"]')) {
        return false;
    }
    return true;
}

// Decide which blocks to hide so the rasterized canvas (and thus the PDF) has
// no blank-page tail and no gappy runs of stray blank lines:
//   • ALL trailing visually-empty blocks are hidden (a final empty paragraph —
//     TrailingBlockPlugin's caret-landing spot, or stray empty <p>s — must not
//     get its own page).
//   • In the INTERIOR, a RUN of 2+ consecutive empties collapses to ONE: the
//     first empty in the run is kept (one intentional blank line is legitimate
//     spacing); every empty after it in the same run is hidden. A LONE interior
//     empty is left untouched — it's almost always deliberate.
// Pure + testable: takes the per-block emptiness flags, returns the sorted
// indices to hide. No DOM, so jsdom (which can't lay out) can still cover it.
export function planEmptyBlockHides(isEmpty: boolean[]): number[] {
    const n = isEmpty.length;
    const hide = new Set<number>();

    // Trailing empties: hide every empty block from the end inward.
    let lastNonEmpty = n - 1;
    while (lastNonEmpty >= 0 && isEmpty[lastNonEmpty]) {
        hide.add(lastNonEmpty);
        lastNonEmpty--;
    }

    // Interior runs: scan only the region before the trailing block (0..lastNonEmpty).
    // Within each maximal run of consecutive empties of length ≥ 2, keep the
    // first, hide the rest.
    let i = 0;
    while (i <= lastNonEmpty) {
        if (!isEmpty[i]) {
            i++;
            continue;
        }
        let j = i;
        while (j <= lastNonEmpty && isEmpty[j]) j++;
        const runLen = j - i;
        if (runLen >= 2) {
            for (let k = i + 1; k < j; k++) hide.add(k); // keep i, hide the rest
        }
        i = j;
    }

    return Array.from(hide).sort((a, b) => a - b);
}

// Compute the page's real bottom edge for fit decisions. A page grows past its
// default height when the EXTEND branch fits a too-tall group; after that, the
// next group must pack against the ACTUAL content bottom, not the frozen
// `pageTop + defaultPageHeightCSS` (the EXTEND coordinate-divergence bug, where
// a small section after a tall table was needlessly pushed to a fresh page).
// Pure + testable. Returns the max of the default page bottom and the real
// content bottom (+ bottom padding), so a non-extended page behaves exactly as
// before while an extended page reflects its true height.
export function pageNaturalBottom(pageTop: number, contentEnd: number, defaultPageHeightCSS: number): number {
    const defaultBottom = pageTop + defaultPageHeightCSS;
    const contentBottom = contentEnd + PAGE_BOTTOM_PADDING_CSS;
    return Math.max(defaultBottom, contentBottom);
}

function planPages(contentEl: HTMLElement, defaultPageHeightCSS: number): { ranges: PageRange[]; edits: MarginEdit[] } {
    const edits: MarginEdit[] = [];
    const internalRanges: PageRangeInternal[] = [];

    const rawBlocks = Array.from(contentEl.children).filter(
        (c) => c instanceof HTMLElement && c.getAttribute('data-slate-node') === 'element',
    ) as HTMLElement[];

    // Hide blank-line noise BEFORE pagination so the rasterized canvas (and the
    // PDF) gets neither a blank-page tail nor gappy runs of stray empties:
    //   • ALL trailing visually-empty blocks (TrailingBlockPlugin's caret spot,
    //     stray trailing `<p>`s) — they must not get their own page.
    //   • Interior RUNS of 2+ consecutive empties collapse to one (keep a single
    //     intentional blank line). A lone interior empty is left as-is.
    // `planEmptyBlockHides` decides which indices to hide (pure + unit-tested).
    // We `display: none` the hidden blocks AND drop them from the `blocks` list:
    // they're excluded from pagination, but `contentEl.offsetHeight` would still
    // include them otherwise and the rasterized canvas captures the full content
    // height — hiding shrinks the canvas so the PDF carries no extra whitespace.
    // `edits` carries the restore so this is fully reversible.
    const emptyFlags = rawBlocks.map(isVisuallyEmptyBlock);
    const hideIdx = new Set(planEmptyBlockHides(emptyFlags));
    let hiddenCount = 0;
    const blocks: HTMLElement[] = [];
    for (let i = 0; i < rawBlocks.length; i++) {
        const block = rawBlocks[i];
        if (hideIdx.has(i)) {
            edits.push({ el: block, previous: block.style.marginTop, previousDisplay: block.style.display });
            block.style.display = 'none';
            hiddenCount++;
        } else {
            blocks.push(block);
        }
    }
    const trimmedTail = hiddenCount;
    // Force layout flush after hiding so subsequent offsetTop/offsetHeight
    // reads reflect the shrunk contentEl.
    if (hiddenCount > 0) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        contentEl.offsetHeight;
    }

    let pageTop = 0;
    let hasBlockOnCurrentPage = false;
    let currentPageContentEnd = 0;

    plogGroup(`run start`);
    plog('defaultPageHeightCSS', Math.round(defaultPageHeightCSS), 'px');
    plog('contentEl total height', Math.round(contentEl.offsetHeight), 'px');
    plog('total blocks', rawBlocks.length, `(${blocks.length} after trimming ${trimmedTail} trailing empty)`);
    plog('PAGE_TOP_PADDING_CSS', PAGE_TOP_PADDING_CSS, 'PAGE_BOTTOM_PADDING_CSS', PAGE_BOTTOM_PADDING_CSS);
    plogGroupEnd();

    // If after trimming there's nothing to paginate, emit a single minimum-sized
    // page and return. The slice loop downstream tolerates a single empty range
    // (it'll render a blank PDF page, but the document had zero content so
    // that's the only honest output). Sized to the minimum (not a full default
    // page) so a zero-content export isn't a needlessly tall blank sheet.
    if (blocks.length === 0) {
        return { ranges: [{ topCSS: 0, heightCSS: MIN_PAGE_HEIGHT_CSS }], edits };
    }

    const blockBottom = (b: HTMLElement) => b.offsetTop + b.offsetHeight;

    const pushBlockToY = (block: HTMLElement, targetTop: number, naturalTop: number) => {
        // The block's `offsetTop` (naturalTop) already includes its CSS
        // margin-top. Setting `style.marginTop = pushPx` OVERRIDES the CSS
        // margin entirely, so the block would land at
        //   preceding_sibling.bottom + pushPx
        // = (naturalTop - cssMarginTop) + pushPx
        // = targetTop - cssMarginTop
        // i.e. cssMarginTop pixels above its intended position. For an h2
        // with margin-top: 20px that's a ~20px shift that causes the top
        // of the heading to bleed onto the previous page and the table
        // below it to start mid-row on the next page (the user-visible
        // "table split across pages" bug). Restore the CSS margin by
        // adding it back into the inline override.
        const computed = window.getComputedStyle(block);
        const cssMarginTop = parseFloat(computed.marginTop) || 0;
        const pushPx = targetTop - naturalTop;
        edits.push({ el: block, previous: block.style.marginTop });
        block.style.marginTop = `${pushPx + cssMarginTop}px`;
        // Force a layout flush so subsequent `offsetTop` reads in the loop
        // reflect the push immediately, not after the next idle tick.
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        block.offsetHeight;
    };

    // Commits the CURRENT page (whatever content has accumulated up to
    // `currentPageContentEnd`) as a content-sized page, then starts a fresh
    // page beginning at `firstBlock`. Extracted out of the overflow PUSH
    // branch below into its own function for readability.
    const pushGroupToNewPage = (firstBlockToPush: HTMLElement, lastBlockOnPage: HTMLElement, naturalTopOfPush: number) => {
        const contentHeight = currentPageContentEnd - pageTop;
        const pageHeight = contentSizedPageHeight(contentHeight);
        internalRanges.push({ topCSS: pageTop, heightCSS: pageHeight, _contentHeight: contentHeight });

        plog(
            `→ PUSH to new page | committed page#${currentPageIdx}: top=${Math.round(pageTop)} height=${Math.round(
                pageHeight,
            )} contentHeight=${Math.round(contentHeight)} (gap=${Math.round(
                pageHeight - contentHeight - PAGE_BOTTOM_PADDING_CSS,
            )}px on this page)`,
        );

        const newPageTop = pageTop + pageHeight;
        const newPageContentTop = newPageTop + PAGE_TOP_PADDING_CSS;

        pushBlockToY(firstBlockToPush, newPageContentTop, naturalTopOfPush);
        pageTop = newPageTop;
        // Re-read from DOM after the push flush.
        currentPageContentEnd = blockBottom(lastBlockOnPage);
        hasBlockOnCurrentPage = true;
        currentPageIdx++;
        plog(`  new page#${currentPageIdx} starts at ${Math.round(pageTop)}, content end now ${Math.round(currentPageContentEnd)}`);
    };

    // Build groups first so the main loop operates on sticky units rather than
    // individual blocks. This eliminates the old findStickyGroupStart /
    // canPullGroup logic and fixes the orphaned-heading-first-on-page bug.
    const groups = buildPaginationGroups(blocks);

    plogGroup(`groups built (${groups.length} groups from ${blocks.length} blocks)`);
    if (PLAN_PAGES_DEBUG) {
        groups.forEach((g, idx) => {
            const head = blocks[g.startIdx];
            const tail = blocks[g.endIdx];
            const h = tail.offsetTop + tail.offsetHeight - head.offsetTop;
            const tags = blocks
                .slice(g.startIdx, g.endIdx + 1)
                .map((b) => b.tagName)
                .join('+');
            plog(`group#${idx} blocks[${g.startIdx}..${g.endIdx}] (${tags}) height=${Math.round(h)}px head=${describeBlock(head)}`);
        });
    }
    plogGroupEnd();

    let currentPageIdx = 0;
    for (let gi = 0; gi < groups.length; gi++) {
        const group = groups[gi];
        const firstBlock = blocks[group.startIdx];
        const lastBlock = blocks[group.endIdx];

        // Skip zero-height blocks (hidden or collapsed).
        if (firstBlock.offsetHeight <= 0) {
            plog(`group#${gi} SKIP (zero height) ${describeBlock(firstBlock)}`);
            continue;
        }

        const naturalTop = firstBlock.offsetTop;

        // Re-read after any previous push flush — lastBlock may have moved.
        const naturalEnd = lastBlock.offsetTop + lastBlock.offsetHeight;
        const groupHeight = naturalEnd - naturalTop;
        // The page's REAL bottom: its default height, OR — if a prior EXTEND grew
        // it past that — the actual content bottom. Using the real bottom (not the
        // frozen `pageTop + defaultPageHeightCSS`) is the fix for the EXTEND
        // coordinate-divergence bug: a small section after a tall extended table
        // now packs against the table's true bottom instead of being pushed to a
        // fresh page. On a non-extended page this equals `pageTop + default…`, so
        // behaviour is unchanged there.
        const pageDefaultBottom = pageNaturalBottom(pageTop, currentPageContentEnd, defaultPageHeightCSS);

        // Change 3: strict fit test subtracts bottom padding so content
        // never packs against the absolute page bottom edge.
        const strictFitBottom = pageDefaultBottom - PAGE_BOTTOM_PADDING_CSS;

        plogGroup(
            `group#${gi} → page#${currentPageIdx} | h=${Math.round(groupHeight)}px | naturalTop=${Math.round(
                naturalTop,
            )} naturalEnd=${Math.round(naturalEnd)} | pageTop=${Math.round(pageTop)} strictBottom=${Math.round(
                strictFitBottom,
            )} | ${describeBlock(firstBlock)}`,
        );

        if (!hasBlockOnCurrentPage || naturalEnd <= strictFitBottom) {
            // Group fits on the current page within the bottom-padding budget.
            const reason = !hasBlockOnCurrentPage ? 'first block on page' : 'fits within strict bottom';
            plog(`✓ STRICT-FIT (${reason}) — currentPageContentEnd now ${Math.round(naturalEnd)}`);
            currentPageContentEnd = naturalEnd;
            hasBlockOnCurrentPage = true;
        } else if (groupHeight <= defaultPageHeightCSS - PAGE_TOP_PADDING_CSS - PAGE_BOTTOM_PADDING_CSS) {
            // Group fits on a page by itself (not a tall block). Decide whether
            // to force-fit it into the current page's bottom-padding zone or
            // push it to a new page.

            // Change 2: backstop force-fit — if pushing would leave a large
            // empty gap (> 30 % of the page) AND the group only slightly
            // overflows the strict boundary (within the bottom-padding budget),
            // absorb it rather than creating a nearly-empty page.
            const emptyIfPushed = pageDefaultBottom - currentPageContentEnd;
            const overflowAmount = naturalEnd - pageDefaultBottom;
            const looseFit =
                hasBlockOnCurrentPage && emptyIfPushed > 0.3 * defaultPageHeightCSS && overflowAmount <= PAGE_BOTTOM_PADDING_CSS;

            plog(
                `decision needed: emptyIfPushed=${Math.round(emptyIfPushed)} (${Math.round(
                    (emptyIfPushed / defaultPageHeightCSS) * 100,
                )}%) overflow=${Math.round(overflowAmount)}px backstopLimit=${PAGE_BOTTOM_PADDING_CSS}px`,
            );

            if (looseFit) {
                // Force-fit into the bottom-padding zone — treat as fitting.
                plog(`✓ LOOSE-FIT (backstop: gap > 30%, overflow within budget) — absorbed into current page`);
                currentPageContentEnd = naturalEnd;
            } else {
                // Push the whole group (heading + body) to the next page.
                // Because the group already includes its heading, there is no
                // separate "pull" step — firstBlock IS the heading (or the
                // only block in a standalone group).
                pushGroupToNewPage(firstBlock, lastBlock, naturalTop);
            }
        } else {
            // Group is too tall for any single default-height page — extend the
            // current page to fit it. Tall tables stay together (no row split)
            // by growing the page rather than slicing mid-block. Heading
            // immediately above this block is already on the current page, so
            // it stays glued.
            // Edge case: pageTop === 0 and first group is tall → page extends,
            // same as before.
            plog(
                `⤢ EXTEND page (group taller than usable page: ${Math.round(groupHeight)}px > ${Math.round(
                    defaultPageHeightCSS - PAGE_TOP_PADDING_CSS - PAGE_BOTTOM_PADDING_CSS,
                )}px usable)`,
            );
            currentPageContentEnd = naturalEnd;
            hasBlockOnCurrentPage = true;
        }
        plogGroupEnd();
    }

    if (hasBlockOnCurrentPage) {
        // Content-sized final page — no default-height floor (the source of the
        // trailing blank space). Floored at the sane minimum so a one-line last
        // page isn't degenerate.
        const contentHeight = currentPageContentEnd - pageTop;
        const finalHeight = contentSizedPageHeight(contentHeight);
        internalRanges.push({ topCSS: pageTop, heightCSS: finalHeight, _contentHeight: contentHeight });
        plog(
            `final page#${currentPageIdx}: top=${Math.round(pageTop)} height=${Math.round(finalHeight)} contentHeight=${Math.round(
                contentHeight,
            )} (gap=${Math.round(finalHeight - contentHeight - PAGE_BOTTOM_PADDING_CSS)}px)`,
        );
    } else {
        internalRanges.push({ topCSS: 0, heightCSS: MIN_PAGE_HEIGHT_CSS, _contentHeight: 0 });
        plog(`no blocks rendered — emitting one empty minimum-size page`);
    }

    // Change 4: drop trailing near-empty ranges (content height below threshold).
    // These arise when pushBlockToY math leaves a tiny tail range with effectively
    // no content — e.g. a forced-push that moved everything to the previous page.
    const EMPTY_RANGE_THRESHOLD = PAGE_TOP_PADDING_CSS + 24;
    let droppedTrailing = 0;
    while (internalRanges.length > 1) {
        const last = internalRanges[internalRanges.length - 1];
        if (last._contentHeight < EMPTY_RANGE_THRESHOLD) {
            internalRanges.pop();
            droppedTrailing++;
        } else {
            break;
        }
    }
    if (droppedTrailing > 0) plog(`dropped ${droppedTrailing} trailing near-empty page(s) (< ${EMPTY_RANGE_THRESHOLD}px content)`);

    plogGroup(`done — ${internalRanges.length} pages`);
    internalRanges.forEach((r, i) => {
        plog(
            `page#${i}: top=${Math.round(r.topCSS)} height=${Math.round(r.heightCSS)} contentHeight=${Math.round(
                r._contentHeight,
            )} gap=${Math.round(r.heightCSS - r._contentHeight - PAGE_BOTTOM_PADDING_CSS)}px`,
        );
    });
    plogGroupEnd();

    // Strip internal field before returning — callers only expect PageRange.
    const ranges: PageRange[] = internalRanges.map(({ topCSS, heightCSS }) => ({ topCSS, heightCSS }));

    return { ranges, edits };
}

function restoreMarginEdits(edits: MarginEdit[]) {
    for (const e of edits) {
        e.el.style.marginTop = e.previous;
        if (e.previousDisplay !== undefined) {
            e.el.style.display = e.previousDisplay;
        }
    }
}

// Fonts the editor can render but which load lazily (only once text using them
// is laid out): Pacifico is the script face used for signatures (Google Fonts
// @import in src/scss/index.scss); Gilroy is the brand body face (@font-face in
// the same file). `document.fonts.ready` alone only waits for loads ALREADY in
// flight — it does NOT trigger an un-started load — so on a machine where the
// signature's Pacifico hasn't finished downloading at capture time, html2canvas
// rasterizes it in a fallback font. Force-loading each face guarantees it's
// decoded and available to the canvas, so the exported signature matches the
// editor exactly on every machine.
//
// Exported (RECUR-27304) so exportToPdfServer.ts's inlineFonts() can reuse the
// SAME "the app always needs these two brand faces, regardless of the page"
// list as its usage-filter's always-embed set — one definition of the app's
// default fonts, not two.
export const EDITOR_FONT_FAMILIES = [
    'Pacifico',
    'Gilroy-Light',
    'Gilroy-Medium',
    'Gilroy-Regular',
    'Gilroy-SemiBold',
    'Gilroy-Bold',
    'Gilroy-ExtraBold',
];

async function forceLoadEditorFonts(contentEl: HTMLElement, timeoutMs = 4000): Promise<void> {
    const fontSet: any = (document as any).fonts;
    if (!fontSet?.load) return;

    const families = new Set<string>(EDITOR_FONT_FAMILIES);
    // Also force-load any explicit font-family the content actually uses — the
    // FontFamilyPlugin renders font-family marks as inline styles on leaves, so
    // a custom signature/body font picked from the toolbar gets covered too.
    contentEl.querySelectorAll<HTMLElement>('[style*="font-family"]').forEach((el) => {
        const head = el.style.fontFamily?.split(',')[0]?.replace(/['"]/g, '').trim();
        if (head) families.add(head);
    });

    const loads: Array<Promise<unknown>> = [];
    families.forEach((family) => {
        const p = fontSet.load(`16px "${family}"`);
        if (p?.catch) loads.push(p.catch(() => undefined));
    });

    const ready = (async () => {
        try {
            await Promise.all(loads);
            await fontSet.ready;
        } catch {
            /* best-effort */
        }
    })();
    await Promise.race([ready, new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))]);
}

export async function exportEditorToPdf({
    target,
    filename = 'document',
    singlePage = false,
    returnBlob = false,
    tiled = false,
}: ExportToPdfOptions): Promise<Blob | void> {
    const [{ default: html2canvas }, PDFLib] = await Promise.all([import('html2canvas-pro'), import('pdf-lib')]);

    const contentEl =
        (target.querySelector('[data-testid="plate-editor-content"]') as HTMLElement | null) ??
        (target.querySelector('[contenteditable]') as HTMLElement | null) ??
        target;

    const backups = expandForCapture(target);

    // Force-load every font the editor renders (incl. the signature's Pacifico)
    // before measurement + capture. Beyond matching the signature font exactly,
    // this also stabilises pagination: if a web font is still loading when
    // planPages reads block heights, the later reflow shifts content 1-3px per
    // line and tables start mid-row on the next page. Force-loading makes the
    // measured layout the final layout.
    await forceLoadEditorFonts(contentEl);

    // Pre-fetch images as base64 to bypass CORS on S3 presigned URLs.
    const [docIdBase64Map, otherImagesMap] = await Promise.all([resolveDocIdImagesToBase64(contentEl), preloadImagesAsDataUrls(contentEl)]);

    // Wait for every <img> currently in the live editor to FINISH decoding
    // before we measure block heights. Without this, `block.offsetHeight`
    // can return the placeholder height (the <img>'s naturalHeight is 0
    // until the bytes are decoded), planPages plans pages too tight, and
    // html2canvas later snapshots with the fully-decoded images at their
    // real heights — pushing subsequent in-cell content past where the
    // PDF slice ends. The classic symptom: image renders but the text in
    // the same cell beneath/beside it is gone.
    await Promise.all(
        Array.from(contentEl.querySelectorAll('img')).map((img) => {
            const node = img as HTMLImageElement;
            // .decode() resolves when the image has been fully decoded and is
            // ready to paint. Use .complete as a fast path; fall through to
            // a load promise for the rest. Errors are swallowed — a broken
            // image still has a placeholder height we can measure.
            if (node.complete && node.naturalHeight > 0) return Promise.resolve();
            return (
                node.decode
                    ? node.decode()
                    : new Promise<void>((resolve) => {
                          const done = () => resolve();
                          node.addEventListener('load', done, { once: true });
                          node.addEventListener('error', done, { once: true });
                      })
            ).catch(() => undefined);
        }),
    );
    // Force a layout flush so block.offsetTop / offsetHeight reflect the
    // post-decode state before planPages reads them.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    contentEl.offsetHeight;
    const defaultPageHeightCSS = getDefaultPageHeightPx(contentEl.offsetWidth || contentEl.scrollWidth || 1229);
    // singlePage mode: bypass planPages entirely — one range covering the
    // whole editor scrollHeight. `edits` stays empty so restoreMarginEdits
    // is a no-op. The slice loop below produces one PDF page sized to the
    // full canvas; no widow/orphan logic, no marginTop pushes, no sticky
    // heading handling — there are no page boundaries to defend against.
    const { ranges, edits } = singlePage
        ? { ranges: [{ topCSS: 0, heightCSS: contentEl.scrollHeight }] as PageRange[], edits: [] as MarginEdit[] }
        : planPages(contentEl, defaultPageHeightCSS);

    // ── Auto-tile + capture-scale decision (fixes #3, #4) ────────────────────
    // singlePage deliberately makes ONE tall canvas (its own semantics) — never
    // auto-tile or auto-scale it. For paginated exports:
    //   #3 AUTO-TILE: a single html2canvas canvas silently truncates past the
    //      browser canvas wall (~16384px/side on Safari ≈ ~4-5 pages at scale 3).
    //      Auto-enable tiling whenever total content — or any single range —
    //      exceeds the safe per-tile height, so long docs work without the caller
    //      passing `tiled`. The explicit `tiled` option still forces tiling.
    //   #4 OVERSIZED GROUP: a single range taller than ONE tile (e.g. a table >
    //      ~4,666 CSS px at scale 3) can't be captured whole at full scale. Rather
    //      than silently truncate, auto-scale the WHOLE capture down so the tallest
    //      range fits one tile (smaller but COMPLETE) and warn. If even the minimum
    //      scale can't fit it, throw — never drop content.
    const autoTile = !singlePage && shouldAutoTile(ranges, MAX_TILE_CANVAS_PX, SNAPSHOT_SCALE);
    const useTiled = (tiled || autoTile) && !singlePage;

    let captureScale = SNAPSHOT_SCALE;
    if (useTiled) {
        const fitScale = scaleToFitTallestRange(ranges, MAX_TILE_CANVAS_PX, SNAPSHOT_SCALE);
        if (fitScale < SNAPSHOT_SCALE) {
            if (fitScale < MIN_AUTOSCALE) {
                const tallest = ranges.reduce((m, r) => Math.max(m, r.heightCSS), 0);
                restoreMarginEdits(edits);
                restoreAfterCapture(backups);
                throw new Error(
                    `[exportToPdf] a single block group is ${Math.round(tallest)} CSS px tall — too tall to ` +
                        `rasterize whole even at the minimum capture scale (${MIN_AUTOSCALE}x) without exceeding the ` +
                        `browser canvas-height wall (${MAX_TILE_CANVAS_PX}px). Split the oversized table/section, ` +
                        `or export via the paged exporter. Refusing to silently truncate content.`,
                );
            }
            captureScale = fitScale;
        }
    }

    // Collect link rects AFTER pagination so positions reflect the adjusted
    // layout (a link in a pushed block ends up at its new Y). Use captureScale
    // (not the raw const) so link canvas coords match an auto-scaled capture.
    // Force a layout pass before measuring — the marginTop edits we just
    // applied need to flush before getBoundingClientRect / offsetTop are
    // accurate.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    contentEl.offsetHeight;
    const links = collectLinks(contentEl, captureScale);

    // Compute the largest Y we plan to slice from + a safety buffer. We use
    // MAX(scrollHeight, last_range.bottom + 200) so html2canvas has enough
    // vertical room even if (a) the clone reflows slightly taller than the
    // live editor or (b) the planned last page is taller than scrollHeight
    // because it was clamped to the default min. The buffer is cheap —
    // unused canvas height is just white pixels — but truncation here
    // silently drops content from the bottom of the PDF.
    const plannedBottomCSS = ranges.length > 0 ? ranges[ranges.length - 1].topCSS + ranges[ranges.length - 1].heightCSS : 0;
    const windowHeightCSS = Math.max(contentEl.scrollHeight, plannedBottomCSS) + 200;

    // Stage 0 of tiled capture (M1): the html2canvas `onclone` mutation pass is
    // hoisted into a named handler so it can run on EVERY tile's clone — tiled
    // capture invokes html2canvas once per vertical band, and each band is an
    // independent clone that must be styled/stripped identically. Behaviour is
    // byte-for-byte identical to the previous inline closure.
    const oncloneHandler = (doc: Document) => {
        const editorElement = doc.querySelector('[data-testid="plate-editor-content"]') ?? contentEl;
        if (editorElement) {
            // Strip the page-card chrome (border + rounded corners +
            // drop-shadow) from the snapshot. Those styles exist so the
            // editor visually reads as a "sheet of paper" against the
            // gray canvas in the live UI, but they have no business in
            // the exported PDF — html2canvas-pro otherwise rasterises
            // them as a thin gray line + soft shadow around every page.
            (editorElement as HTMLElement).style.border = 'none';
            (editorElement as HTMLElement).style.borderRadius = '0';
            (editorElement as HTMLElement).style.boxShadow = 'none';

            // Suppress the cell-selection blue tint inside the snapshot.
            // The .cellSelected class paints a `::after` overlay; we
            // also strip the class below, but CSS-module hashing makes
            // class-name matching fragile across builds — a style tag
            // overrides the pseudo-element regardless of the hash.
            // Same defense for the image "selected" ring.
            const overrideStyle = doc.createElement('style');
            overrideStyle.textContent = `
                        [class*="cellSelected"]::after,
                        [class*="cell-selected"]::after {
                            display: none !important;
                            background: transparent !important;
                            content: none !important;
                        }
                        [class*="imageContainerSelected"] {
                            box-shadow: none !important;
                            outline: none !important;
                        }
                        /* Strip the <hr> node's selection ring/tint. If an hr is
                           the selected node at export time, its purple
                           box-shadow + background would otherwise rasterize into
                           the PDF (this was the one selection-chrome class with
                           no strip). */
                        [class*="hrHostSelected"] {
                            box-shadow: none !important;
                            outline: none !important;
                            background: transparent !important;
                        }
                        /* The live editor wraps every table in a horizontal
                           scroll box (.tableScroll, RECUR-26175). html2canvas-pro
                           rasterises a scroll container at its layout box and
                           CLIPS overflowing content — a wide table would lose its
                           off-screen columns in the PDF. Force the wrapper open so
                           the whole table is captured, matching the pre-wrapper
                           behaviour for wide tables. */
                        [class*="tableScroll"] {
                            overflow: visible !important;
                        }
                    `;
            doc.head.appendChild(overrideStyle);

            // Bullet/highlight rasterization fix — html2canvas-pro paints `::marker` and
            // `<mark>` bg against the line-height box; pin them to glyph baseline for export only.
            const rasterFixStyle = doc.createElement('style');
            rasterFixStyle.textContent = `
                        /* PDF export — match the live editor's tight list
                           rhythm. Plate v53 wraps EACH bullet in its own
                           <ul>/<ol>; html2canvas-pro doesn't apply the live
                           sheet's :has() collapse rule so we zero margins on
                           every list wrapper, and pin descendant line-height
                           to 1.2 (mirrors the live .content rules). */
                        ul, ol {
                            margin: 0 !important;
                            line-height: 1.2;
                        }
                        li {
                            margin: 0 !important;
                            padding: 0 !important;
                            line-height: 1.2;
                        }
                        li p,
                        li div,
                        li span {
                            margin: 0 !important;
                            line-height: 1.2;
                        }
                        ul > li::marker,
                        ol > li::marker {
                            font-size: 1em !important;
                            color: #37352f;
                            line-height: 1.2;
                        }
                        /* Match the live editor's ordered-number style
                           (PlateEditor.module.scss): normal body weight +
                           tabular figures so the exported PDF numbering looks
                           identical. */
                        ol > li::marker {
                            font-weight: 400 !important;
                            font-variant-numeric: tabular-nums;
                        }
                        /* Right/center/justify aligned list items — mirror the
                           live editor's BlockList fix. The CSS-module rule in
                           .content sets these without !important; html2canvas-pro
                           is more conservative with [data-align] attribute
                           selectors so we re-state with !important to make
                           sure the rasterised output matches the live editor. */
                        li[data-align] {
                            list-style-position: inside !important;
                        }
                        li[data-align="right"] {
                            text-align: right !important;
                        }
                        li[data-align="center"] {
                            text-align: center !important;
                        }
                        li[data-align="justify"] {
                            text-align: justify !important;
                        }
                        li[data-align] > p {
                            display: inline !important;
                            padding-left: 0 !important;
                        }
                        ul[style*='list-style-type: square'] > li::before {
                            top: 0.5em !important;
                        }
                        /* PDF export — anchor highlight background tightly to text.
                         * Covers both Plate's HighlightPlugin (<mark>) AND
                         * FontBackgroundColorPlugin (inline <span style="background-color:…">).
                         * box-decoration-break: clone is load-bearing — without it,
                         * html2canvas-pro drops the glyphs from preceding line-fragments
                         * when a highlighted span wraps across multiple visual lines
                         * (bg paints fine, text disappears). With clone, each line
                         * fragment is treated as its own decorated box and rasterizes
                         * its glyphs correctly. */
                        mark,
                        span[style*="background-color"] {
                            padding: 0.05em 0 !important;
                            background-clip: content-box;
                            -webkit-box-decoration-break: clone;
                            box-decoration-break: clone;
                        }
                    `;
            doc.head.appendChild(rasterFixStyle);

            // Real glyph-drop fix: rewrite long wrapping highlighted runs into
            // per-word single-line spans so each takes html2canvas's clean
            // single-rect path. The CSS above is the fallback for what this skips.
            splitHighlightRunsForRaster(editorElement as HTMLElement);

            editorElement.querySelectorAll('img').forEach((img) => {
                const docId = img.getAttribute('data-doc-id');
                const orig = img.getAttribute('src') || (img as HTMLImageElement).src;
                // Prefer the docId-resolved base64 (fresh presigned →
                // fetch). Fall back to whatever the raw-URL preloader
                // managed to grab.
                const dataUrl = (docId ? docIdBase64Map.get(docId) : null) || (orig ? otherImagesMap.get(orig) : null);
                if (dataUrl) img.setAttribute('src', dataUrl);
            });

            // Strip editor chrome that leaks into the PDF snapshot:
            //   - the × delete button + the 4 corner resize handles
            //     (rendered by ImageElement only when the image is
            //     currently "selected" in the editor — html2canvas
            //     captures whatever is in the live DOM)
            //   - the blue selection outline (set via box-shadow on
            //     `.imageContainerSelected`)
            // Both are pure editor UI and have no business in the
            // exported PDF. CSS modules hash class names so we match
            // by substring rather than the literal class string.
            editorElement
                .querySelectorAll<HTMLElement>('[class*="ImageElement_deleteBtn"], [class*="ImageElement_handle"]')
                .forEach((el) => el.remove());
            editorElement.querySelectorAll<HTMLElement>('[class*="ImageElement_imageContainerSelected"]').forEach((el) => {
                // Drop the selected-state class so its box-shadow
                // doesn't draw a blue ring around the image.
                el.className = el.className
                    .split(/\s+/)
                    .filter((cls) => !/ImageElement_imageContainerSelected/.test(cls))
                    .join(' ');
            });

            // Strip table cell-selection chrome — multi-cell drag-select
            // adds an `rgba(35,131,226,0.08)` overlay via a `::after`
            // pseudo-element on the `cellSelected` class. The class
            // also paints when the caret rests on a cell edge during
            // resize, which is when the user notices the blue tint
            // leaking into the PDF. CSS modules hash the class name
            // (e.g. `tableElements_cellSelected__abc`), match by
            // substring.
            editorElement.querySelectorAll<HTMLElement>('[class*="tableElements_cellSelected"]').forEach((el) => {
                el.className = el.className
                    .split(/\s+/)
                    .filter((cls) => !/tableElements_cellSelected/.test(cls))
                    .join(' ');
            });
            // Same treatment for the table-level `tableSelecting` class,
            // which suppresses carets but is irrelevant in the PDF.
            editorElement.querySelectorAll<HTMLElement>('[class*="tableElements_tableSelecting"]').forEach((el) => {
                el.className = el.className
                    .split(/\s+/)
                    .filter((cls) => !/tableElements_tableSelecting/.test(cls))
                    .join(' ');
            });
            // Resize handles render only on hover but stay in DOM with
            // pointer-events listeners; their visible 1-px hover line
            // (::after) can paint into the snapshot if the user's
            // Remove editor-only resize handles.
            editorElement
                .querySelectorAll<HTMLElement>(
                    '[class*="tableElements_colHandle"], [class*="tableElements_rowHandle"], [class*="tableElements_leftHandle"], [class*="ImageWrapMenu"], [class*="ImageElement_uploadChip"], [class*="ImageElement_uploadError"], [class*="ImageElement_handle"]',
                )
                .forEach((el) => el.remove());

            // Force combined text-decoration inline — html2canvas can override CSS selectors.
            editorElement.querySelectorAll<HTMLElement>('[data-slate-leaf="true"]').forEach((leaf) => {
                let hasU = false;
                let hasS = false;
                const all = [leaf, ...Array.from(leaf.querySelectorAll<HTMLElement>('*'))];
                let p: HTMLElement | null = leaf.parentElement;
                while (p && p !== editorElement) {
                    all.push(p);
                    p = p.parentElement;
                }
                for (const el of all) {
                    const tag = el.tagName.toLowerCase();
                    if (tag === 'u') hasU = true;
                    if (tag === 's' || tag === 'del') hasS = true;
                    if (hasU && hasS) break;
                }
                if (hasU && hasS) {
                    const existing = leaf.getAttribute('style') || '';
                    leaf.setAttribute(
                        'style',
                        `${existing}; text-decoration: underline line-through !important; text-decoration-line: underline line-through !important;`,
                    );
                }
            });
            // Do NOT override font-family — different widths cause clone height drift and page truncation.
        }
    };

    // `useTiled` and `captureScale` were decided above (auto-tile + oversized-
    // group scale). singlePage always falls through to the single-canvas path —
    // its one-tall-page semantics are incompatible with tile-boundary logic, and
    // it's excluded from both the auto-tile and auto-scale decisions.

    if (!useTiled) {
        // ── SINGLE-CANVAS PATH ───────────────────────────────────────────────
        // Reached only when content fits one safe canvas (or singlePage). No
        // range can be oversized here, so captureScale === SNAPSHOT_SCALE.
        let canvas: HTMLCanvasElement;
        try {
            canvas = await html2canvas(contentEl, {
                backgroundColor: '#ffffff',
                scale: captureScale,
                useCORS: true,
                allowTaint: false,
                logging: false,
                windowHeight: windowHeightCSS,
                windowWidth: contentEl.scrollWidth,
                onclone: oncloneHandler,
            });
        } finally {
            restoreMarginEdits(edits);
            restoreAfterCapture(backups);
        }

        const pdfDoc = await PDFLib.PDFDocument.create();
        const PDFName = PDFLib.PDFName;
        const PDFString = PDFLib.PDFString;

        const pxToPt = PDF_CONTENT_WIDTH_PT / canvas.width;

        // ── Snap interior page breaks to whitespace ──────────────────────────────
        // `planPages` computes boundaries in LIVE-editor CSS px, but html2canvas
        // rasterizes a re-laid-out CLONE that drifts vertically further down the
        // doc. Applying live-space boundaries to the drifted raster lands deep cuts
        // inside heading glyphs. Fix: for each INTERIOR boundary, find the nearest
        // all-white scanline in the rendered canvas and cut there instead. A
        // heading always has a white top-margin band, so (with upward bias) the cut
        // lands in that band and the whole heading flows to the next page.
        //
        // Tiling contract: page i covers [snapped[i], snapped[i+1]). The first
        // boundary (top, 0) and the last (canvas bottom) are anchors and never
        // snapped. With 0 interior boundaries (singlePage, or a one-page doc) this
        // whole block is a no-op and slicing is identical to before.
        const snapWindowPx = Math.round(0.3 * defaultPageHeightCSS * captureScale);
        const boundaries: number[] = [0];
        for (let i = 1; i < ranges.length; i++) {
            boundaries.push(Math.round(ranges[i].topCSS * captureScale));
        }
        boundaries.push(canvas.height);

        const snapped = boundaries.slice();
        if (boundaries.length > 2) {
            const fullCtx = canvas.getContext('2d');
            if (fullCtx) {
                const COLUMN_STEP = 4;
                const WHITE_MIN = 250;
                // Only read pixels within each interior boundary's search window —
                // reading the whole canvas would be slow on tall multi-page docs.
                const makeIsBlank = (center: number): ((y: number) => boolean) => {
                    const bandTop = Math.max(0, center - snapWindowPx);
                    const bandBottom = Math.min(canvas.height, center + snapWindowPx + 1);
                    const bandHeight = bandBottom - bandTop;
                    if (bandHeight <= 0) return () => false;
                    const band = fullCtx.getImageData(0, bandTop, canvas.width, bandHeight).data;
                    return (y: number): boolean => {
                        if (y < bandTop || y >= bandBottom) return false;
                        const rowStart = (y - bandTop) * canvas.width * 4;
                        for (let x = 0; x < canvas.width; x += COLUMN_STEP) {
                            const px = rowStart + x * 4;
                            if (band[px] < WHITE_MIN || band[px + 1] < WHITE_MIN || band[px + 2] < WHITE_MIN) {
                                return false;
                            }
                        }
                        return true;
                    };
                };
                for (let i = 1; i < boundaries.length - 1; i++) {
                    const planned = boundaries[i];
                    snapped[i] = snapToBlankRow(makeIsBlank(planned), planned, snapWindowPx, canvas.height);
                }
                // Keep boundaries STRICTLY increasing — an upward snap must not
                // cross (or touch) the previous (already-snapped) boundary, which
                // would create a negative- or zero-height slice. Reset to the
                // un-snapped planned boundary first; if that STILL isn't strictly
                // greater (possible when the previous boundary snapped downward
                // past it), force a minimal +1px gap so every slice has real area.
                for (let i = 1; i < snapped.length - 1; i++) {
                    if (snapped[i] <= snapped[i - 1]) {
                        snapped[i] = Math.max(boundaries[i], snapped[i - 1] + 1);
                    }
                }
            }
        }

        for (let pageIdx = 0; pageIdx < snapped.length - 1; pageIdx++) {
            const range = ranges[pageIdx];
            const sliceTopCanvas = snapped[pageIdx];
            const sliceBottomCanvas = snapped[pageIdx + 1];
            const sliceHeightCanvas = Math.max(1, sliceBottomCanvas - sliceTopCanvas);
            // Pages are content-sized now (planPages drops the default-height
            // floor), so range.heightCSS is the content height + bottom padding.
            // The last page uses the planned content-sized height so a short final
            // page isn't shrink-wrapped below its true content; interior pages use
            // the snapped slice height directly.
            const isLastPage = pageIdx === snapped.length - 2;
            const plannedFullCanvas = range
                ? Math.ceil((range.topCSS + range.heightCSS) * captureScale) - sliceTopCanvas
                : sliceHeightCanvas;
            const plannedPageHeightCanvas = isLastPage ? Math.max(sliceHeightCanvas, plannedFullCanvas) : sliceHeightCanvas;

            const sliceCanvas = document.createElement('canvas');
            sliceCanvas.width = canvas.width;
            sliceCanvas.height = sliceHeightCanvas;
            const ctx = sliceCanvas.getContext('2d');
            // A null 2D context means the browser refused a canvas (out of memory /
            // context limit). `break`-ing here would silently drop this page AND
            // every page after it, yielding a short PDF with no error. Fail loud
            // instead so the export surfaces the problem rather than truncating.
            if (!ctx)
                throw new Error(
                    `[exportToPdf] could not get 2D context for page ${pageIdx + 1}/${snapped.length - 1} (canvas allocation failed)`,
                );
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
            ctx.drawImage(canvas, 0, -sliceTopCanvas);

            const png = await pdfDoc.embedPng(sliceCanvas.toDataURL('image/png'));
            const drawHpt = sliceHeightCanvas * pxToPt;
            const plannedHpt = plannedPageHeightCanvas * pxToPt;
            const pageHeightPt = plannedHpt + MARGIN_PT * 2;
            const page = pdfDoc.addPage([PAGE_WIDTH_PT, pageHeightPt]);
            // PDF coords are origin-bottom-left.
            const imageBottomPt = pageHeightPt - MARGIN_PT - drawHpt;
            page.drawImage(png, {
                x: MARGIN_PT,
                y: imageBottomPt,
                width: PDF_CONTENT_WIDTH_PT,
                height: drawHpt,
            });

            const pageLinks = links.filter((l) => l.y + l.h > sliceTopCanvas && l.y < sliceBottomCanvas);
            if (pageLinks.length) {
                const annotRefs: any[] = [];
                for (const l of pageLinks) {
                    const topInSlice = l.y - sliceTopCanvas;
                    const x1Pt = MARGIN_PT + l.x * pxToPt;
                    const x2Pt = x1Pt + l.w * pxToPt;
                    // PDF y is bottom-left origin.
                    const yTopPt = imageBottomPt + drawHpt - topInSlice * pxToPt;
                    const y1Pt = yTopPt - l.h * pxToPt;
                    const y2Pt = yTopPt;
                    const annot = pdfDoc.context.register(
                        pdfDoc.context.obj({
                            Type: 'Annot',
                            Subtype: 'Link',
                            Rect: [x1Pt, y1Pt, x2Pt, y2Pt],
                            Border: [0, 0, 0],
                            A: pdfDoc.context.obj({
                                Type: 'Action',
                                S: 'URI',
                                URI: PDFString.of(l.url),
                            }),
                        }),
                    );
                    annotRefs.push(annot);
                }
                (page.node as any).set(PDFName.of('Annots'), pdfDoc.context.obj(annotRefs));
            }
        }

        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });

        if (returnBlob) return blob;

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.pdf`;
        document.body.append(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
        return;
    }

    // ── TILED CAPTURE PATH (M1) ───────────────────────────────────────────────
    // Chunks the document into vertical bands of ≤MAX_TILE_CANVAS_PX raster px
    // so no single html2canvas call produces a canvas taller than Safari's
    // 16,384 px/side wall. Each tile covers ~4 A3 pages at SNAPSHOT_SCALE=3.
    // Reached for explicit `tiled` OR auto-tiled long docs (fix #3). `captureScale`
    // is SNAPSHOT_SCALE unless a single oversized group forced it down (fix #4).
    //
    // REUSE contract:
    //   - ranges, links, defaultPageHeightCSS, windowHeightCSS — shared with the
    //     existing path (already computed above).
    //   - oncloneHandler — applied to every tile's clone.
    //   - pxToPt, MARGIN_PT, PDF_CONTENT_WIDTH_PT, PAGE_WIDTH_PT — unchanged.
    //   - snapToBlankRow — unchanged.
    //   - captureScale — the ACTUAL capture scale (== SNAPSHOT_SCALE unless an
    //     oversized group forced an auto-scale-down). ALL tile/abs-coordinate math
    //     uses captureScale so tiling + slicing stay consistent with the capture.
    //   - restoreMarginEdits, restoreAfterCapture — called ONCE in finally.

    // buildTiles MUST use captureScale: if scale was reduced for an oversized
    // group, the tile ceiling in CSS px grows, and the oversized range now fits a
    // single tile. With the requested scale it would (wrongly) still be flagged.
    const tiles = buildTiles(ranges, MAX_TILE_CANVAS_PX, captureScale);

    // Capture each tile sequentially — concurrent full-doc iframes spike memory.
    // Each call to html2canvas clones + lays out the WHOLE document but bounds the
    // output canvas to [x:0, y:tile.topCSS, width:scrollWidth, height:tile.heightCSS].
    const captured: { tile: Tile; canvas: HTMLCanvasElement }[] = [];
    try {
        for (const tile of tiles) {
            const tileCanvas = await html2canvas(contentEl, {
                backgroundColor: '#ffffff',
                scale: captureScale,
                useCORS: true,
                allowTaint: false,
                logging: false,
                x: 0,
                y: tile.topCSS,
                width: contentEl.scrollWidth,
                height: tile.heightCSS,
                // Pass FULL doc height so every tile's clone lays out the entire
                // document — identical layout context across all tiles.
                windowWidth: contentEl.scrollWidth,
                windowHeight: windowHeightCSS,
                onclone: oncloneHandler,
            });
            captured.push({ tile, canvas: tileCanvas });
        }
    } finally {
        // Restore DOM mutations ONCE after all tiles complete (mirrors the
        // existing path's finally block placement).
        restoreMarginEdits(edits);
        restoreAfterCapture(backups);
    }

    // pxToPt is relative to canvas width (constant across tiles because all
    // tiles share the same scrollWidth × captureScale).
    const tileCanvasWidth = captured.length > 0 ? captured[0].canvas.width : contentEl.scrollWidth * captureScale;
    const pxToPt = PDF_CONTENT_WIDTH_PT / tileCanvasWidth;

    const pdfDoc = await PDFLib.PDFDocument.create();
    const PDFName = PDFLib.PDFName;
    const PDFString = PDFLib.PDFString;

    // ── Build absolute boundaries in canvas px ───────────────────────────────
    // Absolute canvas Y for a CSS y: absY = cssY * captureScale.
    // Tile t covers absolute [t.topCSS*scale, (t.topCSS+t.heightCSS)*scale).
    // Tile-local Y for an absolute Y: tileLocalY = absY - t.topCSS*scale.
    //
    // Boundaries array has one entry per page-range boundary:
    //   boundaries[0]                 = 0               (top anchor, never snapped)
    //   boundaries[1..ranges.length-1] = ranges[i].topCSS * scale (interior cuts)
    //   boundaries[ranges.length]     = lastRange.bottom * scale  (bottom anchor, never snapped)
    // Bottom anchor mirrors the non-tiled path's `boundaries.push(canvas.height)`:
    // use the LAST tile's ACTUAL captured pixel height (tileTop + canvas.height)
    // rather than the PLANNED range bottom. They differ only by sub-pixel
    // rounding today, but anchoring to real pixels avoids clipping the final
    // page's last rows when the last range was grown by the EXTEND branch.
    const lastCapture = captured[captured.length - 1];
    const lastBottomAbs = Math.round(lastCapture.tile.topCSS * captureScale) + lastCapture.canvas.height;

    const tiledBoundaries: number[] = [0];
    for (let i = 1; i < ranges.length; i++) {
        tiledBoundaries.push(Math.round(ranges[i].topCSS * captureScale));
    }
    tiledBoundaries.push(lastBottomAbs);

    const tiledSnapped = tiledBoundaries.slice();

    // ── Per-tile snapping of interior boundaries ─────────────────────────────
    // Only snap INTERIOR boundaries (index 1 .. boundaries.length-2).
    // A boundary that exactly coincides with a tile seam (tile.topCSS*scale)
    // is a clean inter-tile cut — skip snapping for those (they're already
    // at a page boundary with no content to drift into).
    const snapWindowPx = Math.round(0.3 * defaultPageHeightCSS * captureScale);
    const COLUMN_STEP = 4;
    const WHITE_MIN = 250;

    // Build a Set of tile-seam abs Y values for the "skip seam" guard.
    const tileSeamAbs = new Set<number>(captured.map(({ tile }) => Math.round(tile.topCSS * captureScale)));

    for (let bi = 1; bi < tiledBoundaries.length - 1; bi++) {
        const plannedAbs = tiledBoundaries[bi];

        // A tile seam is a clean cut — no snapping needed.
        if (tileSeamAbs.has(plannedAbs)) continue;

        // Find the tile that owns this boundary (the tile whose abs range
        // contains plannedAbs).
        const ownerEntry = captured.find(({ tile }) => {
            const tileTopAbs = Math.round(tile.topCSS * captureScale);
            const tileBottomAbs = Math.round((tile.topCSS + tile.heightCSS) * captureScale);
            return plannedAbs >= tileTopAbs && plannedAbs < tileBottomAbs;
        });

        if (!ownerEntry) continue; // degenerate — boundary outside all tiles, leave as-is

        const { tile: ownerTile, canvas: tileCanvas } = ownerEntry;
        const tileTopAbs = Math.round(ownerTile.topCSS * captureScale);

        const tileCtx = tileCanvas.getContext('2d');
        if (!tileCtx) continue;

        // Convert the planned abs boundary to tile-local canvas Y.
        const plannedTileLocal = plannedAbs - tileTopAbs;
        const tileCanvasHeight = tileCanvas.height;

        // makeIsBlank reads from this tile's canvas in tile-local coordinates.
        const makeTileIsBlank = (centerLocal: number): ((y: number) => boolean) => {
            const bandTop = Math.max(0, centerLocal - snapWindowPx);
            const bandBottom = Math.min(tileCanvasHeight, centerLocal + snapWindowPx + 1);
            const bandHeight = bandBottom - bandTop;
            if (bandHeight <= 0) return () => false;
            const band = tileCtx.getImageData(0, bandTop, tileCanvas.width, bandHeight).data;
            return (localY: number): boolean => {
                if (localY < bandTop || localY >= bandBottom) return false;
                const rowStart = (localY - bandTop) * tileCanvas.width * 4;
                for (let x = 0; x < tileCanvas.width; x += COLUMN_STEP) {
                    const px = rowStart + x * 4;
                    if (band[px] < WHITE_MIN || band[px + 1] < WHITE_MIN || band[px + 2] < WHITE_MIN) {
                        return false;
                    }
                }
                return true;
            };
        };

        const snappedTileLocal = snapToBlankRow(makeTileIsBlank(plannedTileLocal), plannedTileLocal, snapWindowPx, tileCanvasHeight - 1);

        // Convert back to absolute canvas Y.
        tiledSnapped[bi] = snappedTileLocal + tileTopAbs;
    }

    // Strict-monotonic guard — same as existing path.
    for (let i = 1; i < tiledSnapped.length - 1; i++) {
        if (tiledSnapped[i] <= tiledSnapped[i - 1]) {
            tiledSnapped[i] = Math.max(tiledBoundaries[i], tiledSnapped[i - 1] + 1);
        }
    }

    // ── Slice loop: one PDF page per range ───────────────────────────────────
    // For each page range, we find the owning tile (the tile whose pageIdxStart
    // ≤ pageIdx ≤ pageIdxEnd), create a slice canvas that draws from that tile
    // at tile-local coordinates, and assemble the PDF page identically to the
    // existing path. Links are filtered/placed in absolute canvas Y (unchanged).
    for (let pageIdx = 0; pageIdx < tiledSnapped.length - 1; pageIdx++) {
        const range = ranges[pageIdx];
        const sliceTopAbs = tiledSnapped[pageIdx];
        const sliceBottomAbs = tiledSnapped[pageIdx + 1];
        const sliceHeightCanvas = Math.max(1, sliceBottomAbs - sliceTopAbs);

        const isLastPage = pageIdx === tiledSnapped.length - 2;
        const plannedFullCanvas = range ? Math.ceil((range.topCSS + range.heightCSS) * captureScale) - sliceTopAbs : sliceHeightCanvas;
        const plannedPageHeightCanvas = isLastPage ? Math.max(sliceHeightCanvas, plannedFullCanvas) : sliceHeightCanvas;

        // Find the tile that owns this page range.
        const ownerEntry = captured.find(({ tile }) => tile.pageIdxStart <= pageIdx && pageIdx <= tile.pageIdxEnd);
        if (!ownerEntry) {
            // Unreachable given buildTiles guarantees full coverage — but if it
            // ever happens, fail LOUD rather than silently dropping a page (a
            // short PDF with no error is worse than a thrown export).
            throw new Error(`[exportToPdf tiled] no owning tile for page ${pageIdx + 1}/${tiledSnapped.length - 1}`);
        }

        const { tile: ownerTile, canvas: tileCanvas } = ownerEntry;
        const tileTopAbs = Math.round(ownerTile.topCSS * captureScale);

        // Tile-local Y of the slice top within the owning tile's canvas.
        const sliceTopTileLocal = sliceTopAbs - tileTopAbs;

        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = tileCanvas.width;
        sliceCanvas.height = sliceHeightCanvas;
        const ctx = sliceCanvas.getContext('2d');
        if (!ctx)
            throw new Error(
                `[exportToPdf tiled] could not get 2D context for page ${pageIdx + 1}/${
                    tiledSnapped.length - 1
                } (canvas allocation failed)`,
            );
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        // Draw the owning tile's canvas offset by the tile-local slice top.
        ctx.drawImage(tileCanvas, 0, -sliceTopTileLocal);

        const png = await pdfDoc.embedPng(sliceCanvas.toDataURL('image/png'));
        const drawHpt = sliceHeightCanvas * pxToPt;
        const plannedHpt = plannedPageHeightCanvas * pxToPt;
        const pageHeightPt = plannedHpt + MARGIN_PT * 2;
        const page = pdfDoc.addPage([PAGE_WIDTH_PT, pageHeightPt]);
        // PDF coords are origin-bottom-left.
        const imageBottomPt = pageHeightPt - MARGIN_PT - drawHpt;
        page.drawImage(png, {
            x: MARGIN_PT,
            y: imageBottomPt,
            width: PDF_CONTENT_WIDTH_PT,
            height: drawHpt,
        });

        // Link annotations — filtered/placed in absolute canvas Y, identical to
        // the existing path. sliceTopAbs / sliceBottomAbs are the absolute bounds.
        const pageLinks = links.filter((l) => l.y + l.h > sliceTopAbs && l.y < sliceBottomAbs);
        if (pageLinks.length) {
            const annotRefs: any[] = [];
            for (const l of pageLinks) {
                const topInSlice = l.y - sliceTopAbs;
                const x1Pt = MARGIN_PT + l.x * pxToPt;
                const x2Pt = x1Pt + l.w * pxToPt;
                // PDF y is bottom-left origin.
                const yTopPt = imageBottomPt + drawHpt - topInSlice * pxToPt;
                const y1Pt = yTopPt - l.h * pxToPt;
                const y2Pt = yTopPt;
                const annot = pdfDoc.context.register(
                    pdfDoc.context.obj({
                        Type: 'Annot',
                        Subtype: 'Link',
                        Rect: [x1Pt, y1Pt, x2Pt, y2Pt],
                        Border: [0, 0, 0],
                        A: pdfDoc.context.obj({
                            Type: 'Action',
                            S: 'URI',
                            URI: PDFString.of(l.url),
                        }),
                    }),
                );
                annotRefs.push(annot);
            }
            (page.node as any).set(PDFName.of('Annots'), pdfDoc.context.obj(annotRefs));
        }
    }

    const pdfBytesTiled = await pdfDoc.save();
    const blobTiled = new Blob([pdfBytesTiled], { type: 'application/pdf' });

    if (returnBlob) return blobTiled;

    const urlTiled = URL.createObjectURL(blobTiled);
    const linkEl = document.createElement('a');
    linkEl.href = urlTiled;
    linkEl.download = `${filename}.pdf`;
    document.body.append(linkEl);
    linkEl.click();
    linkEl.remove();
    setTimeout(() => URL.revokeObjectURL(urlTiled), 30_000);
}
